import { Box, HStack, Icon, Link } from "@chakra-ui/react";
import CDS from "design-system";
import { Link as RouterLink } from "react-router";
import { LuCircleCheck, LuCircleMinus } from "react-icons/lu";
import { Task } from "features/Resources/Task/models";
import { NewHireJourney } from "models/v2/newHireJourney";
import { useNewHireJourneyDetailQuery } from "features/NewHireJourney/hooks";
import { userPathById } from "helpers/url/pages/v1";

const ROLE_LABELS: Record<string, string> = {
  manager: "Manager",
  buddy: "Buddy",
  people_team_contact: "HR Contact",
  recruiter: "Recruiter",
  it_team_contact: "IT Team Contact",
  new_hire: "New Hire",
};

interface TaskActionOutcomeProps {
  task: Task;
}

function Callout({
  variant, children,
}: {
  variant: "success" | "neutral";
  children: React.ReactNode;
}) {
  const success = variant === "success";
  return (
    <Box
      bg={success ? "green.50" : "gray.50"}
      borderLeftWidth="3px"
      borderLeftColor={success ? "green.500" : "gray.400"}
      borderRadius="md"
      px={3}
      py={2.5}
      mb={3}
    >
      <HStack spacing={2} align="start">
        <Icon
          as={success ? LuCircleCheck : LuCircleMinus}
          color={success ? "green.600" : "gray.500"}
          boxSize={4}
          mt="2px"
          flexShrink={0}
        />
        <Box flex={1} minW={0}>{children}</Box>
      </HStack>
    </Box>
  );
}

function NothingHappened({ verb }: { verb: string }) {
  return (
    <Callout variant="neutral">
      <CDS.Text12 color="content.secondary" fontStyle="italic">
        {`Closed without ${verb}.`}
      </CDS.Text12>
    </Callout>
  );
}

function resolveRecipientLabel(task: Task, journey: NewHireJourney | undefined): string | undefined {
  if (task.messageRecipientType === "User" && task.messageRecipientId) {
    const stakeholders = [journey?.newHire, journey?.manager, journey?.buddy, journey?.peopleTeamContact];
    const match = stakeholders.find((u) => u?.id === task.messageRecipientId);
    if (match?.fullName) return match.fullName;
  }
  if (task.messageRecipientRole) {
    return ROLE_LABELS[task.messageRecipientRole as string] ?? task.messageRecipientRole;
  }
  return undefined;
}

/**
 * For closed tasks with a task_action, render the outcome of that action
 * as a sentence (e.g. "Kevin Berry was selected as the buddy"). If the
 * task closed without actually doing the action, render a "Closed without..."
 * sentence instead.
 */
export default function TaskActionOutcome({ task }: TaskActionOutcomeProps) {
  const { data: journey, isLoading } = useNewHireJourneyDetailQuery(task.newHireJourneyId, {
    enabled: !!task.newHireJourneyId && !!task.taskAction,
  });

  if (!task.taskAction) return null;
  if (isLoading) return null;

  const completed = task.status === "completed";

  switch (task.taskAction) {
    case "assign_buddy": {
      const buddy = journey?.buddy;
      if (!completed || !buddy) return <NothingHappened verb="selecting a buddy" />;
      return (
        <Callout variant="success">
          <CDS.Text12 color="content.primary">
            <Link
              as={RouterLink}
              to={userPathById(buddy.id)}
              fontWeight="semibold"
              color="blue.600"
              _hover={{ textDecoration: "underline" }}
            >
              {buddy.fullName}
            </Link>
            {" was selected as the buddy."}
          </CDS.Text12>
        </Callout>
      );
    }
    case "create_onboarding_plan": {
      const url = journey?.onboardingPlanUrl;
      if (!completed || !url) return <NothingHappened verb="setting an onboarding plan" />;
      return (
        <Callout variant="success">
          <CDS.Text12 color="content.primary">
            {"The onboarding plan is "}
            <Link
              href={url}
              isExternal
              color="blue.600"
              fontWeight="semibold"
              _hover={{ textDecoration: "underline" }}
            >
              {url}
            </Link>
            .
          </CDS.Text12>
        </Callout>
      );
    }
    case "create_email_account": {
      const email = journey?.newHire?.workEmail;
      if (!completed || !email) return <NothingHappened verb="recording a work email" />;
      return (
        <Callout variant="success">
          <CDS.Text12 color="content.primary">
            {"The work email is "}
            <Link
              href={`mailto:${email}`}
              color="blue.600"
              fontWeight="semibold"
              _hover={{ textDecoration: "underline" }}
            >
              {email}
            </Link>
            .
          </CDS.Text12>
        </Callout>
      );
    }
    case "send_message": {
      if (!completed) return <NothingHappened verb="sending a message" />;
      const senderName = task.assignee?.fullName;
      const recipientName = resolveRecipientLabel(task, journey);
      return (
        <Callout variant="success">
          <CDS.Text12 color="content.primary">
            {senderName && recipientName
              ? `Message sent from ${senderName} to ${recipientName}.`
              : "Message was sent."}
          </CDS.Text12>
        </Callout>
      );
    }
    default:
      return null;
  }
}
